import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";
import axios from "axios";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  Alert,
  Box,
  Button,
  Card,
  
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  styled,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import {
  AssessmentOutlined,
  Close,
  DownloadOutlined,
  FilterAltOutlined,
  PictureAsPdfOutlined,
  RestartAlt,
} from "@mui/icons-material";

import {
  usePdfSettings,
  DEFAULT_PDF_COLORS,
} from "../../../layouts/PdfSettingsContext";
import { useBranch } from "../../../layouts/BranchContext.js";

/* =========================================================
   API
========================================================= */

const BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://wims.revisetoexcel.com"
).replace(/\/api\/?$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

API.interceptors.request.use((config) => {
  const token =
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("token");

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  }

  return config;
});

/* =========================================================
   COMMON HELPERS
========================================================= */

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const getUser = () => {
  for (const key of ["user", "userData", "currentUser"]) {
    try {
      const raw =
        sessionStorage.getItem(key) ||
        localStorage.getItem(key);

      if (raw) {
        return JSON.parse(raw) || {};
      }
    } catch (error) {
      console.error("User parse error:", error);
    }
  }

  return {};
};

const getTodayString = () =>
  new Date().toISOString().split("T")[0];

const getAbsoluteImageUrl = (value) => {
  if (!value) return "";

  const imageValue =
    typeof value === "object"
      ? value?.url ||
        value?.image ||
        value?.path ||
        value?.src ||
        ""
      : value;

  if (!imageValue) return "";

  const raw = String(imageValue);

  if (/^data:image\//i.test(raw)) {
    return raw;
  }

  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);

      if (["localhost", "127.0.0.1", "0.0.0.0"].includes(parsed.hostname)) {
        const base = String(BASE_URL)
          .replace(/\/api\/?$/i, "")
          .replace(/\/$/, "");

        return `${base}${parsed.pathname}${parsed.search}`;
      }
    } catch {
      // Keep the original URL if parsing fails.
    }

    return raw;
  }

  const base = String(BASE_URL)
    .replace(/\/api\/?$/i, "")
    .replace(/\/$/, "");

  return `${base}${raw.startsWith("/") ? "" : "/"}${raw}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const convertDataUrlToPng = (dataUrl) =>
  new Promise((resolve, reject) => {
    if (!dataUrl || !String(dataUrl).startsWith("data:image/")) {
      resolve("");
      return;
    }

    if (String(dataUrl).startsWith("data:image/png")) {
      resolve(dataUrl);
      return;
    }

    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width || 1;
        canvas.height = image.naturalHeight || image.height || 1;

        const context = canvas.getContext("2d");

        if (!context) {
          reject(new Error("Unable to create canvas context."));
          return;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () =>
      reject(new Error("Unable to convert image to PNG."));

    image.src = dataUrl;
  });

const imageUrlToDataUrl = async (url) => {
  if (!url) return "";

  if (String(url).startsWith("data:image/")) {
    return convertDataUrlToPng(url);
  }

  const response = await API.get(url, {
    responseType: "blob",
  });

  const dataUrl = await blobToDataUrl(response.data);
  return convertDataUrlToPng(dataUrl);
};

const getOneMonthAgoString = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().split("T")[0];
};

const labelize = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.map(formatValue).join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(
        ([key, val]) =>
          `${labelize(key)}: ${formatValue(val)}`
      )
      .join(" | ");
  }

  return String(value);
};

const formatDate = (value) => {
  if (!value) return "—";

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-GB");
};



const normalizeHex = (
  value,
  fallback = DEFAULT_PDF_COLORS.primary
) => {
  let hex = String(value || fallback)
    .replace("#", "")
    .trim();

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (
    hex.length !== 6 ||
    !/^[0-9a-fA-F]{6}$/.test(hex)
  ) {
    return fallback;
  }

  return `#${hex.toUpperCase()}`;
};

const hexToRgb = (hex) => {
  const safe = normalizeHex(hex).replace("#", "");

  return [
    parseInt(safe.slice(0, 2), 16) || 0,
    parseInt(safe.slice(2, 4), 16) || 0,
    parseInt(safe.slice(4, 6), 16) || 0,
  ];
};

const errorMessage = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 401) {
    return "Authentication failed. Please login again.";
  }

  if (status === 403) {
    return "You do not have permission to access this report.";
  }

  if (status === 404) {
    return "Report API endpoint was not found.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (data?.detail) {
    return data.detail;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.error) {
    return formatValue(data.error);
  }

  return error?.message || "Something went wrong.";
};

const getBranchId = (branch) => {
  const rawId =
    branch?.id ??
    branch?.branch_id ??
    branch?.value ??
    branch?.branch?.id ??
    "";

  if (rawId && typeof rawId === "object") {
    return rawId?.id ?? "";
  }

  return rawId;
};

const getBranchName = (branch) => {
  const name =
    branch?.name ??
    branch?.branch_name;

  if (
    typeof name === "string" ||
    typeof name === "number"
  ) {
    return String(name);
  }

  if (name && typeof name === "object") {
    return (
      name?.name ||
      name?.branch_name ||
      (name?.id != null
        ? `Branch ${name.id}`
        : "Unknown Branch")
    );
  }

  return `Branch ${getBranchId(branch)}`;
};

const getSelectionLabel = (item) => {
  const value =
    item?.candidate_name ??
    item?.enquiry_name ??
    item?.name ??
    item?.title ??
    "Unknown";

  let label = "Unknown";

  if (
    typeof value === "string" ||
    typeof value === "number"
  ) {
    label = String(value);
  } else if (
    value &&
    typeof value === "object"
  ) {
    label =
      value?.name ||
      value?.candidate_name ||
      value?.enquiry_name ||
      (value?.id != null
        ? `Item ${value.id}`
        : "Unknown");
  }

  return item?.enquiry_code
    ? `${label} (${item.enquiry_code})`
    : label;
};

const getEnquiryColumns = () => [
  "branch_name",
  "enquiry_name",
  "followup_medium",
  "source_name",
  "course_name",
  "consultant_name",
  "lead_position",
  "enquiry_date",
];

const getFollowUpColumns = () => [
  "enquiry_name",
  "counsellor_name",
  "followup_date",
  "remark",
];

const mapEnquiryRow = (item, index) => ({
  id:
    item?.id ??
    `${index}-${item?.enquiry_code ?? "enquiry"}`,

  serial_no: index + 1,

  branch_name: (() => {
    const branch =
      item?.branch_name ??
      item?.branch;

    if (
      typeof branch === "string" ||
      typeof branch === "number"
    ) {
      return String(branch);
    }

    if (branch && typeof branch === "object") {
      return (
        branch?.name ||
        branch?.branch_name ||
        (branch?.id != null
          ? `Branch ${branch.id}`
          : "")
      );
    }

    return "";
  })(),

  enquiry_name:
    item?.enquiry_name ||
    item?.enquiry ||
    item?.candidate_name ||
    "",

  followup_medium:
    item?.followup_medium ||
    item?.followUpMedium ||
    "",

  source_name:
    item?.source_name ||
    item?.source ||
    "",

  course_name:
    item?.course_name ||
    item?.course ||
    "",

  consultant_name:
    item?.consultant_name ||
    item?.consultant ||
    item?.assigned_to_name ||
    "",

  lead_position:
    item?.lead_position ||
    item?.leadPosition ||
    "",

  enquiry_date:
    item?.enquiry_date ||
    item?.date ||
    "",
});

const mapFollowUpRow = (item, index) => ({
  id:
    item?.id ??
    `${index}-${item?.enquiry_code ?? "followup"}`,

  serial_no: index + 1,

  enquiry_name:
    item?.enquiry_name ||
    item?.enquiry ||
    item?.student_name ||
    item?.candidate_name ||
    "",

  counsellor_name:
    item?.counsellor_name ||
    item?.counsellor ||
    item?.counsellorId ||
    item?.assigned_to_name ||
    item?.assigned_to ||
    "",

  followup_date:
    item?.followup_date ||
    item?.followUpDate ||
    item?.next_followup_date ||
    item?.date ||
    "",

  remark:
    item?.remark ||
    item?.remarks ||
    item?.followup_remarks ||
    item?.notes ||
    "",
});

const matchesSearch = (row, query) => {
  if (!query) return true;

  return Object.values(row || {})
    .map(formatValue)
    .join(" ")
    .toLowerCase()
    .includes(query.toLowerCase());
};

/* =========================================================
   PROFESSIONAL TABLE
========================================================= */

const CustomTableScrollbar = styled("div")(
  ({ theme }) => ({
    width: "100%",
    overflowX: "auto",
    overflowY: "auto",

    "&::-webkit-scrollbar": {
      width: 7,
      height: 8,
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.24)"
          : "rgba(15,23,42,0.22)",
      borderRadius: 10,
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(15,23,42,0.04)",
    },

    scrollbarWidth: "thin",
  })
);

function ReportTable({
  rows = [],
  columns = [],
  loading = false,
  search,
  onSearch,
  title,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  useEffect(() => {
    setPage(0);
  }, [search, rows.length]);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        matchesSearch(
          row,
          search.trim()
        )
      ),
    [rows, search]
  );

  const paginatedRows = useMemo(
    () =>
      filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredRows, page, rowsPerPage]
  );

  const display = (row, column) => {
    if (column.includes("date")) {
      return formatDate(row?.[column]);
    }

    return formatValue(row?.[column]);
  };

  return (
 <Box
  sx={{
    mt: 2,
    border: "none",
    borderRadius: 0,
    overflow: "hidden",
    backgroundColor: "background.paper",
    boxShadow: "none",
  }}
>
  {/* TABLE CONTAINER */}
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{
      borderRadius: 0,
      backgroundColor: "background.paper",
      boxShadow: "none",
    }}
  >
    <CustomTableScrollbar>
      <Table
        stickyHeader
        size="small"
        sx={{
          minWidth: 1120,

          "& .MuiTableCell-root": {
            borderColor: "divider",
          },

          /* Keep all table rows solid */
          "& .MuiTableBody-root .MuiTableRow-root": {
            backgroundColor: "background.paper",
          },

          "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
            backgroundColor: "background.paper",
          },

          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 4,
                minWidth: 58,
                width: 58,
                fontWeight: 700,
                fontSize: ".78rem",
                whiteSpace: "nowrap",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                py: 1.7,
                px: 1.5,
              }}
            >
              #
            </TableCell>

            {columns.map((column) => (
              <TableCell
                key={column}
                sx={{
                  minWidth: 145,
                  fontWeight: 700,
                  fontSize: ".78rem",
                  whiteSpace: "nowrap",
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  py: 1.7,
                  px: 2,

                  "&.MuiTableCell-stickyHeader": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                {labelize(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow
              sx={{
                backgroundColor: "background.paper",

                "&:hover": {
                  backgroundColor: "background.paper",
                },

                "& .MuiTableCell-root": {
                  backgroundColor: "background.paper",
                },
              }}
            >
              <TableCell
                colSpan={columns.length + 1}
                align="center"
                sx={{
                  py: 8,
                  backgroundColor: "background.paper",
                }}
              >
                <CircularProgress size={30} />

                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.2,
                    color: "text.disabled",
                  }}
                >
                  Loading report...
                </Typography>
              </TableCell>
            </TableRow>
          ) : paginatedRows.length > 0 ? (
            paginatedRows.map((row, index) => (
              <TableRow
                hover
                key={
                  row?.id ??
                  `${page}-${index}`
                }
                sx={{
                  backgroundColor: "background.paper",

                  "&:nth-of-type(even)": {
                    backgroundColor: "background.paper",
                  },

                  "&:hover": {
                    backgroundColor: "action.hover",
                  },

                  "& .MuiTableCell-root": {
                    backgroundColor: "background.paper",
                  },

                  "&:hover .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    backgroundColor: "background.paper",
                    fontWeight: 600,
                    py: 1.55,
                    px: 1.5,
                    fontSize: ".83rem",

                    "tr:hover &": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {page * rowsPerPage + index + 1}
                </TableCell>

                {columns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      py: 1.55,
                      px: 2,
                      fontSize: ".83rem",
                      whiteSpace: "nowrap",
                      color: "text.primary",
                      backgroundColor: "background.paper",

                      "tr:hover &": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    {display(row, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow
              sx={{
                backgroundColor: "background.paper",

                "&:hover": {
                  backgroundColor: "background.paper",
                },

                "& .MuiTableCell-root": {
                  backgroundColor: "background.paper",
                },
              }}
            >
              <TableCell
                colSpan={columns.length + 1}
                align="center"
                sx={{
                  py: 8,
                  backgroundColor: "background.paper",
                }}
              >
                <AssessmentOutlined
                  sx={{
                    fontSize: 40,
                    color: "text.disabled",
                    mb: 1,
                  }}
                />

                <Typography fontWeight={600}>
                  No records found
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: "text.disabled",
                  }}
                >
                  Try changing the filters or search
                  text.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </CustomTableScrollbar>

    <TablePagination
      component="div"
      count={filteredRows.length}
      page={page}
      onPageChange={(_, newPage) =>
        setPage(newPage)
      }
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(
          parseInt(event.target.value, 10)
        );
        setPage(0);
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      sx={{
        borderTop: 1,
        borderColor: "divider",

        ".MuiTablePagination-toolbar": {
          minHeight: 54,
          px: {
            xs: 1,
            md: 2,
          },
        },
      }}
    />
  </TableContainer>
</Box>
  );
}

/* =========================================================
   FILTER MODAL
========================================================= */

function FilterModal({
  open,
  onClose,
  title,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  selectedId,
  onSelectedIdChange,
  options,
  loadingOptions,
  selectionLabel,
  onReset,
  onApply,
}) {
  const theme = useTheme();

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor:
            "rgba(15,23,42,0.58)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform:
            "translate(-50%, -50%)",
          width: {
            xs: "94%",
            sm: 620,
            md: 720,
          },
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          color: "text.primary",
          borderRadius: 3,
          outline: "none",
          boxShadow:
            theme.palette.mode ===
            "dark"
              ? "0 20px 60px rgba(0,0,0,0.55)"
              : "0 20px 60px rgba(0,0,0,0.18)",
          border:
            theme.palette.mode ===
            "dark"
              ? `1px solid ${theme.palette.divider}`
              : "none",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 3,
            py: 2.1,
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
              }}
            >
              {title} Filters
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
              }}
            >
              Configure filters and apply
              the report.
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: 1,
              borderColor:
                "divider",
              borderRadius: 2,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* CONTENT */}
        <Box
          sx={{
            p: 3,
            maxHeight: "60vh",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(50% - 8px)",
                },
              }}
            >
              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  {selectionLabel}
                </InputLabel>

                <Select
                  value={selectedId}
                  label={selectionLabel}
                  onChange={(event) =>
                    onSelectedIdChange(
                      event.target
                        .value
                    )
                  }
                >
                  <MenuItem value="">
                    All {selectionLabel}s
                  </MenuItem>

                  {loadingOptions ? (
                    <MenuItem disabled>
                      Loading...
                    </MenuItem>
                  ) : options.length >
                    0 ? (
                    options.map((item) => (
                      <MenuItem
                        key={item?.id}
                        value={String(
                          item?.id
                        )}
                      >
                        {getSelectionLabel(
                          item
                        )}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      No{" "}
                      {selectionLabel.toLowerCase()}{" "}
                      found
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(50% - 8px)",
                },
              }}
            >
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={dateFrom}
                onChange={(event) =>
                  onDateFromChange(
                    event.target
                      .value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max:
                    dateTo ||
                    undefined,
                }}
              />
            </Box>

            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(50% - 8px)",
                },
              }}
            >
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={dateTo}
                onChange={(event) =>
                  onDateToChange(
                    event.target
                      .value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min:
                    dateFrom ||
                    undefined,
                }}
              />
            </Box>
          </Box>

          <Divider
            sx={{
              mt: 2.5,
              mb: 2,
            }}
          />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 3,
              py: 1.5,
              backgroundColor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              variant="text"
              startIcon={<RestartAlt />}
              onClick={onReset}
              sx={{
                textTransform: "none",
                color: "text.primary",
                fontWeight: 600,
              }}
            >
              Reset
            </Button>

            <Stack
              direction="row"
              spacing={1}
            >
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                }}
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                startIcon={
                  <FilterAltOutlined />
                }
                onClick={onApply}
                disabled={
                  Boolean(
                    dateFrom &&
                    dateTo &&
                    dateFrom >
                      dateTo
                  )
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2.5,
                  fontWeight: 600,
                }}
              >
                Apply Filters
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

/* =========================================================
   PDF COLOR MODAL
========================================================= */

function ColorPickerModal({
  open,
  loading,
  tempThemeColor,
  tempTextColor,
  onClose,
  onThemeChange,
  onTextChange,
  onReset,
  onGenerate,
  reportTitle,
  }) {
  return (
    <ReportPdfColorModal
  open={open}
  onClose={onClose}
  themeColor={tempThemeColor}
  setThemeColor={onThemeChange}
  textColor={tempTextColor}
  setTextColor={onTextChange}
  reportTitle={reportTitle || "Report Title"}
  onGenerate={onGenerate}
  loading={loading}
  disabled={loading}
/>
  );
}

/* =========================================================
   DOWNLOAD MODAL
========================================================= */

function DownloadModal({
  open,
  onClose,
  title,
  onExcel,
  loading,
}) {
  const theme = useTheme();

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor:
            "rgba(15,23,42,0.58)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform:
            "translate(-50%, -50%)",
          width: {
            xs: "92%",
            sm: 560,
          },
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.8,
            backgroundColor:
              theme.palette.primary.main,
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color:
                theme.palette.primary
                  .contrastText,
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>

          <IconButton
            onClick={onClose}
            sx={{
              color:
                theme.palette.primary
                  .contrastText,
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <Stack
          sx={{ p: 3 }}
        >
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={
              <DownloadOutlined />
            }
            onClick={onExcel}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Download Excel
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

/* =========================================================
   REPORT HEADER
========================================================= */

function ReportHeader({
  title,
  subtitle,
  onFilter,
  onGenerate,
  onExcel,
  onReset,
  loading,
  disableExport,
}) {
  return (
  <Box
  sx={{
    px: { xs: 1.5, md: 2.5 },
    py: { xs: 1.5, md: 2 },
    display: "flex",
    flexDirection: {
      xs: "column",
      lg: "row",
    },
    alignItems: {
      xs: "stretch",
      lg: "center",
    },
    justifyContent: "flex-end",
    gap: 2,
  }}
>
  <Stack
    direction={{
      xs: "column",
      sm: "row",
    }}
    spacing={1}
    useFlexGap
    flexWrap="wrap"
    sx={{
      width: {
        xs: "100%",
        lg: "auto",
      },
      justifyContent: {
        xs: "stretch",
        lg: "flex-end",
      },
    }}
  >
    <Button
      variant="contained"
      startIcon={<FilterAltOutlined />}
      onClick={onFilter}
      sx={{
        textTransform: "none",
        borderRadius: 2,
        fontWeight: 600,
      }}
    >
      Filters
    </Button>

    <Button
      variant="contained"
      startIcon={
        loading ? (
          <CircularProgress
            size={17}
            color="inherit"
          />
        ) : (
          <AssessmentOutlined />
        )
      }
      onClick={onGenerate}
      disabled={loading}
      sx={{
        textTransform: "none",
        borderRadius: 2,
        fontWeight: 600,
      }}
    >
      {loading ? "Generating..." : "Generate Report"}
    </Button>

    <Button
      variant="contained"
      color="success"
      startIcon={<DownloadOutlined />}
      onClick={onExcel}
      disabled={loading || disableExport}
      sx={{
        textTransform: "none",
        borderRadius: 2,
        fontWeight: 600,
      }}
    >
      Export Excel
    </Button>

    <Button
      variant="contained"
      startIcon={<RestartAlt />}
      onClick={onReset}
      disabled={loading}
      sx={{
        textTransform: "none",
        borderRadius: 2,
        fontWeight: 600,
      }}
    >
      Reset
    </Button>
  </Stack>
</Box>
  );
}

/* =========================================================
   MAIN
========================================================= */

export default function EnquiryReportsCombined() {
  const theme = useTheme();
  const user = useMemo(
    () => getUser(),
    []
  );

  const {
    selectedBranchId,
    branchList: globalBranchList = [],
    organizationId: globalOrganizationId,
  } = useBranch();

  const globalBranchId = selectedBranchId
    ? String(selectedBranchId)
    : "";

  const organizationId =
    globalOrganizationId ||
    user?.organization_id ||
    user?.organization?.id ||
    "";

  const organizationName =
    user?.organization_name ||
    user?.organizationName ||
    user?.organization?.name ||
    "Organization";

  const contextBranch = useMemo(
    () =>
      (globalBranchList || []).find(
        (branch) =>
          String(getBranchId(branch)) === String(globalBranchId)
      ),
    [globalBranchList, globalBranchId]
  );

  const [branchDetails, setBranchDetails] = useState(null);
  const [branchLogo, setBranchLogo] = useState("");

  const branchName =
    branchDetails?.name ||
    branchDetails?.branch_name ||
    branchDetails?.branchName ||
    (contextBranch ? getBranchName(contextBranch) : "") ||
    (globalBranchId ? `Branch ${globalBranchId}` : "No branch selected");

  useEffect(() => {
    let cancelled = false;

    const loadBranchDetails = async () => {
      if (!globalBranchId) {
        setBranchDetails(null);
        setBranchLogo("");
        return;
      }

      try {
        const response = await API.get(`/api/master/branch/${globalBranchId}/`);
        if (cancelled) return;

        const data = response?.data || {};
        setBranchDetails(data);

        const image =
          data?.image ||
          data?.logo ||
          data?.branch_logo ||
          data?.branchLogo ||
          "";

        if (image) {
          try {
            const dataUrl = await imageUrlToDataUrl(
              getAbsoluteImageUrl(image)
            );
            if (!cancelled) setBranchLogo(dataUrl);
          } catch (logoError) {
            console.error("Branch logo conversion error:", logoError);
            if (!cancelled) setBranchLogo("");
          }
        } else {
          setBranchLogo("");
        }
      } catch (branchError) {
        console.error("Branch details API error:", branchError);
        if (!cancelled) {
          setBranchDetails(null);
          setBranchLogo("");
        }
      }
    };

    loadBranchDetails();

    return () => {
      cancelled = true;
    };
  }, [globalBranchId]);

  const isMainBranchAdmin =
    !!user?.is_main_branch_admin;

  const initialBranchId =
    user?.branch_id
      ? String(user.branch_id)
      : "";

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  /* =======================================================
     DATA
  ======================================================== */

  const [branches, setBranches] =
    useState([]);

  const [enquiryRows, setEnquiryRows] =
    useState([]);

  const [followUpRows, setFollowUpRows] =
    useState([]);

  /* =======================================================
     SEARCH
  ======================================================== */

  const [enquirySearch, setEnquirySearch] =
    useState("");

  const [followUpSearch, setFollowUpSearch] =
    useState("");

  /* =======================================================
     LOADING
  ======================================================== */

  const [enquiryLoading, setEnquiryLoading] =
    useState(false);

  const [followUpLoading, setFollowUpLoading] =
    useState(false);

  const [
    enquiryOptionsLoading,
    setEnquiryOptionsLoading,
  ] = useState(false);

  const [
    followUpOptionsLoading,
    setFollowUpOptionsLoading,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [downloadLoading, setDownloadLoading] =
    useState(false);

  /* =======================================================
     TABS
  ======================================================== */

  const [reportTab, setReportTab] =
    useState("enquiry");

  /* =======================================================
     FILTER STATE
  ======================================================== */

  const [enquiryFilters, setEnquiryFilters] =
    useState({
      dateFrom: getOneMonthAgoString(),
      dateTo: getTodayString(),
      selectedId: "",
    });

  const [followUpFilters, setFollowUpFilters] =
    useState({
      dateFrom: getOneMonthAgoString(),
      dateTo: getTodayString(),
      selectedId: "",
    });


  const [enquiryOptions, setEnquiryOptions] =
    useState([]);

  const [followUpOptions, setFollowUpOptions] =
    useState([]);

  /* =======================================================
     MODALS
  ======================================================== */

  const [enquiryFilterOpen, setEnquiryFilterOpen] =
    useState(false);

  const [followUpFilterOpen, setFollowUpFilterOpen] =
    useState(false);

  const [downloadOpen, setDownloadOpen] =
    useState(null);

  const [colorModalOpen, setColorModalOpen] =
    useState(null);

  const [
    tempPdfThemeColor,
    setTempPdfThemeColor,
  ] = useState(
    normalizeHex(
      pdfThemeColor,
      DEFAULT_PDF_COLORS.primary
    )
  );

  const [
    tempPdfTextColor,
    setTempPdfTextColor,
  ] = useState(
    normalizeHex(
      pdfTextColor,
      DEFAULT_PDF_COLORS.bodyText
    )
  );

  /* =======================================================
     DATE REFS
  ======================================================== */

  const enquiryFromRef =
    useRef(null);

  const enquiryToRef =
    useRef(null);

  const followUpFromRef =
    useRef(null);

  const followUpToRef =
    useRef(null);

  /* =======================================================
     PDF COLOR SYNC
  ======================================================== */

  useEffect(() => {
    if (!colorModalOpen) return;

    setTempPdfThemeColor(
      normalizeHex(
        pdfThemeColor,
        DEFAULT_PDF_COLORS.primary
      )
    );

    setTempPdfTextColor(
      normalizeHex(
        pdfTextColor,
        DEFAULT_PDF_COLORS.bodyText
      )
    );
  }, [
    colorModalOpen,
    pdfThemeColor,
    pdfTextColor,
  ]);

  const notifyError = useCallback(
    (message) => {
      setError(
        message ||
          "Something went wrong."
      );
    },
    []
  );

  /* =======================================================
     BRANCH API
  ======================================================== */

  const fetchBranches =
    useCallback(async () => {
      if (!organizationId) {
        setBranches([]);
        return;
      }

      try {
        const response =
          await API.get(
            `/api/master/organization/${organizationId}/branches/`
          );

        const list =
          normalizeArray(
            response.data
          );

        const valid =
          list.filter(
            (branch) => {
              const branchOrg =
                branch?.organization
                  ?.id ??
                branch?.organization_id ??
                branch?.organizationId ??
                branch?.organization;

              if (
                branchOrg !==
                  null &&
                branchOrg !==
                  undefined &&
                branchOrg !== ""
              ) {
                return (
                  String(
                    branchOrg
                  ) ===
                  String(
                    organizationId
                  )
                );
              }

              return (
                branch?.is_active !==
                false
              );
            }
          );

        setBranches(valid);
      } catch (fetchError) {
        console.error(
          "Branch fetch error:",
          fetchError
        );

        setBranches([]);

        notifyError(
          errorMessage(fetchError)
        );
      }
    }, [
      organizationId,
      notifyError,
    ]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  /* =======================================================
     SOURCE QUERY HELPERS
  ======================================================== */

  const buildEnquiryQueryParams =
    useCallback(
      (
        filters,
        useFollowUpDates = false
      ) => {
        const params = {
          organization:
            organizationId,
        };

        if (globalBranchId) {
          params.branch = globalBranchId;
        }

        if (useFollowUpDates) {
          if (filters?.dateFrom) {
            params.next_followup_from =
              filters.dateFrom;
          }

          if (filters?.dateTo) {
            params.next_followup_to =
              filters.dateTo;
          }
        }

        return params;
      },
      [organizationId, globalBranchId]
    );

  /* =======================================================
     ENQUIRY SOURCE FALLBACK
  ======================================================== */

  const fetchSourceEnquiries =
    useCallback(async () => {
      const response =
        await API.get(
          "/api/student_details/enquiries/",
          {
            params:
              buildEnquiryQueryParams(
                enquiryFilters,
                false
              ),
          }
        );

      let items =
        normalizeArray(
          response.data
        );

      if (enquiryFilters.selectedId) {
        items = items.filter(
          (item) =>
            String(item?.id) ===
            String(
              enquiryFilters.selectedId
            )
        );
      }

      const from =
        enquiryFilters.dateFrom;

      const to =
        enquiryFilters.dateTo;

      if (from || to) {
        items = items.filter(
          (item) => {
            const date =
              item?.enquiry_date ||
              item?.date ||
              "";

            if (!date) return false;

            const value =
              String(date).slice(
                0,
                10
              );

            if (
              from &&
              value < from
            ) {
              return false;
            }

            if (
              to &&
              value > to
            ) {
              return false;
            }

            return true;
          }
        );
      }

      return items.map(
        mapEnquiryRow
      );
    }, [
      buildEnquiryQueryParams,
      enquiryFilters,
    ]);

  /* =======================================================
     FOLLOW UP SOURCE FALLBACK
  ======================================================== */

  const fetchSourceFollowUps =
    useCallback(async () => {
      const response =
        await API.get(
          "/api/student_details/enquiries/",
          {
            params:
              buildEnquiryQueryParams(
                followUpFilters,
                true
              ),
          }
        );

      let items =
        normalizeArray(
          response.data
        );

      if (
        followUpFilters.selectedId
      ) {
        items = items.filter(
          (item) =>
            String(item?.id) ===
            String(
              followUpFilters.selectedId
            )
        );
      }

      const from =
        followUpFilters.dateFrom;

      const to =
        followUpFilters.dateTo;

      items = items.filter(
        (item) => {
          const date =
            item?.next_followup_date ||
            item?.followup_date ||
            "";

          if (!date) return false;

          const value =
            String(date).slice(
              0,
              10
            );

          if (
            from &&
            value < from
          ) {
            return false;
          }

          if (
            to &&
            value > to
          ) {
            return false;
          }

          return true;
        }
      );

      return items.map(
        mapFollowUpRow
      );
    }, [
      buildEnquiryQueryParams,
      followUpFilters,
    ]);

  /* =======================================================
     ENQUIRY OPTIONS
  ======================================================== */

  const loadEnquiryOptions =
    useCallback(async () => {
      if (
        !organizationId ||
        !enquiryFilters.branch
      ) {
        setEnquiryOptions([]);
        return;
      }

      setEnquiryOptionsLoading(
        true
      );

      try {
        const response =
          await API.get(
            "/api/student_details/enquiries/",
            {
              params: {
                organization:
                  organizationId,
                branch:
                  enquiryFilters.branch,
              },
            }
          );

        setEnquiryOptions(
          normalizeArray(
            response.data
          )
        );
      } catch (fetchError) {
        console.error(
          "Enquiry options error:",
          fetchError
        );

        setEnquiryOptions([]);
      } finally {
        setEnquiryOptionsLoading(
          false
        );
      }
    }, [
      organizationId,
      enquiryFilters.branch,
    ]);

  useEffect(() => {
    loadEnquiryOptions();
  }, [loadEnquiryOptions]);

  /* =======================================================
     FOLLOW-UP OPTIONS
  ======================================================== */

  const loadFollowUpOptions =
    useCallback(async () => {
      if (
        !organizationId ||
        !followUpFilters.branch
      ) {
        setFollowUpOptions([]);
        return;
      }

      setFollowUpOptionsLoading(
        true
      );

      try {
        const response =
          await API.get(
            "/api/student_details/enquiries/",
            {
              params:
                buildEnquiryQueryParams(
                  followUpFilters,
                  true
                ),
            }
          );

        setFollowUpOptions(
          normalizeArray(
            response.data
          )
        );
      } catch (fetchError) {
        console.error(
          "Follow-up options error:",
          fetchError
        );

        setFollowUpOptions([]);
      } finally {
        setFollowUpOptionsLoading(
          false
        );
      }
    }, [
      buildEnquiryQueryParams,
      followUpFilters,
      organizationId,
    ]);

  useEffect(() => {
    loadFollowUpOptions();
  }, [loadFollowUpOptions]);

  /* =======================================================
     PAYLOADS
  ======================================================== */

  const enquiryPayload = useMemo(
    () => ({
      fromDate:
        enquiryFilters.dateFrom,
      toDate:
        enquiryFilters.dateTo,
      organizationId,
      branch: globalBranchId,

      ...(enquiryFilters.selectedId
        ? {
            enquiryId:
              enquiryFilters.selectedId,
          }
        : {}),
    }),
    [
      enquiryFilters,
      organizationId,
      globalBranchId,
    ]
  );

  const followUpPayload = useMemo(
    () => ({
      fromDate:
        followUpFilters.dateFrom,
      toDate:
        followUpFilters.dateTo,
      organizationId,
      branch: globalBranchId,

      ...(followUpFilters.selectedId
        ? {
            enquiryId:
              followUpFilters.selectedId,
          }
        : {}),
    }),
    [
      followUpFilters,
      organizationId,
      globalBranchId,
    ]
  );

  /* =======================================================
     ENQUIRY REPORT
  ======================================================== */

  const fetchEnquiryReport =
    useCallback(async () => {
      if (!organizationId) {
        notifyError(
          "Organization ID not found."
        );
        return [];
      }

      setEnquiryLoading(true);
      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/enquiry/",
            enquiryPayload
          );

        let rows =
          normalizeArray(
            response.data
          ).map(
            mapEnquiryRow
          );

        if (!rows.length) {
          try {
            rows =
              await fetchSourceEnquiries();
          } catch (
            fallbackError
          ) {
            console.error(
              "Enquiry source fallback error:",
              fallbackError
            );
          }
        }

        setEnquiryRows(rows);

        if (!rows.length) {
          setError(
            "No enquiry records found for the selected filters."
          );
        }

        return rows;
      } catch (fetchError) {
        console.error(
          "Enquiry report error:",
          fetchError
        );

        try {
          const fallbackRows =
            await fetchSourceEnquiries();

          setEnquiryRows(
            fallbackRows
          );

          if (!fallbackRows.length) {
            notifyError(
              errorMessage(
                fetchError
              )
            );
          }

          return fallbackRows;
        } catch (
          fallbackError
        ) {
          console.error(
            "Enquiry source fallback error:",
            fallbackError
          );

          setEnquiryRows([]);

          notifyError(
            errorMessage(
              fetchError
            )
          );
        }

        return [];
      } finally {
        setEnquiryLoading(false);
      }
    }, [
      enquiryPayload,
      fetchSourceEnquiries,
      notifyError,
      organizationId,
    ]);

  /* =======================================================
     FOLLOW-UP REPORT
  ======================================================== */

  const fetchFollowUpReport =
    useCallback(async () => {
      if (!organizationId) {
        notifyError(
          "Organization ID not found."
        );
        return [];
      }

      setFollowUpLoading(true);
      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/followup/",
            followUpPayload
          );

        let rows =
          normalizeArray(
            response.data
          ).map(
            mapFollowUpRow
          );

        if (!rows.length) {
          try {
            rows =
              await fetchSourceFollowUps();
          } catch (
            fallbackError
          ) {
            console.error(
              "Follow-up source fallback error:",
              fallbackError
            );
          }
        }

        setFollowUpRows(rows);

        if (!rows.length) {
          setError(
            "No follow-up records found for the selected filters."
          );
        }

        return rows;
      } catch (fetchError) {
        console.error(
          "Follow-up report error:",
          fetchError
        );

        try {
          const fallbackRows =
            await fetchSourceFollowUps();

          setFollowUpRows(
            fallbackRows
          );

          if (!fallbackRows.length) {
            notifyError(
              errorMessage(
                fetchError
              )
            );
          }

          return fallbackRows;
        } catch (
          fallbackError
        ) {
          console.error(
            "Follow-up source fallback error:",
            fallbackError
          );

          setFollowUpRows([]);

          notifyError(
            errorMessage(
              fetchError
            )
          );
        }

        return [];
      } finally {
        setFollowUpLoading(
          false
        );
      }
    }, [
      fetchSourceFollowUps,
      followUpPayload,
      notifyError,
      organizationId,
    ]);

  /* =======================================================
     RESET
  ======================================================== */

  const resetEnquiryFilters =
    () => {
      setEnquiryFilters({
        dateFrom:
          getOneMonthAgoString(),
        dateTo:
          getTodayString(),
        selectedId: "",
      });

      setEnquirySearch("");
      setEnquiryRows([]);
      setError("");
    };

  const resetFollowUpFilters =
    () => {
      setFollowUpFilters({
        dateFrom:
          getOneMonthAgoString(),
        dateTo:
          getTodayString(),
        selectedId: "",
      });

      setFollowUpSearch("");
      setFollowUpRows([]);
      setError("");
    };

  /* =======================================================
     BRANCH LABEL
  ======================================================== */

  const getSelectedBranchLabel =
    useCallback(
      (
        branchId,
        fallbackRows
      ) => {
        if (!branchId) {
          return "All Branches";
        }

        const rowBranch =
          fallbackRows?.[0]
            ?.branch_name;

        if (rowBranch) {
          return formatValue(
            rowBranch
          );
        }

        const branch =
          branches.find(
            (item) =>
              String(
                getBranchId(item)
              ) ===
              String(branchId)
          );

        return branch
          ? getBranchName(branch)
          : "Selected Branch";
      },
      [branches]
    );

  /* =======================================================
     PROFESSIONAL PDF CREATOR
  ======================================================== */

  const createPdf = ({
    reportType,
    rows,
    themeColor,
    textColor,
  }) => {
    const isEnquiry = reportType === "enquiry";
    const filters = isEnquiry ? enquiryFilters : followUpFilters;
    const title = isEnquiry
      ? "Enquiry Report"
      : "Enquiry Follow Up Report";
    const columns = isEnquiry
      ? getEnquiryColumns()
      : getFollowUpColumns();

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth =
      doc.internal.pageSize.getWidth();

    const pageHeight =
      doc.internal.pageSize.getHeight();

    const margin = 10;

    const primary = normalizeHex(
      themeColor,
      DEFAULT_PDF_COLORS.primary
    );

    const bodyText = normalizeHex(
      textColor,
      DEFAULT_PDF_COLORS.bodyText
    );

    const primaryRgb = hexToRgb(primary);
    const bodyTextRgb = hexToRgb(bodyText);

    // Header
    doc.setFillColor(...primaryRgb);
    doc.rect(0, 0, pageWidth, 35, "F");

    if (branchLogo) {
      try {
        doc.addImage(
          branchLogo,
          "PNG",
          margin,
          7,
          21,
          21
        );
      } catch (logoError) {
        console.warn(
          "Unable to add dynamic branch logo:",
          logoError
        );
      }
    }

    const centerX = pageWidth / 2;

    doc.setTextColor(
      ...hexToRgb(DEFAULT_PDF_COLORS.headerText)
    );

    doc.setFont(
      "helvetica",
      "bold"
    );
    doc.setFontSize(15);

    doc.text(
      organizationName || "Organization",
      centerX,
      12,
      { align: "center" }
    );

    doc.setFont(
      "helvetica",
      "normal"
    );
    doc.setFontSize(10.5);

    doc.text(
      branchName || "Branch",
      centerX,
      19,
      { align: "center" }
    );

    doc.setFont(
      "helvetica",
      "bold"
    );
    doc.setFontSize(10);

    doc.text(
      title,
      centerX,
      26,
      { align: "center" }
    );

    doc.setFont(
      "helvetica",
      "normal"
    );
    doc.setFontSize(7.5);

    doc.text(
      `Generated: ${new Date().toLocaleString("en-IN")}`,
      pageWidth - margin,
      10,
      { align: "right" }
    );

    // Filters
    let y = 43;

    doc.setTextColor(...primaryRgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text("FILTERS", margin, y);

    const filterText = [
      `Branch: ${branchName || "—"}`,
      `From: ${filters.dateFrom || "All"}`,
      `To: ${filters.dateTo || "All"}`,
    ].join("   |   ");

    doc.setTextColor(...bodyTextRgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);

    const filterLines =
      doc.splitTextToSize(
        filterText,
        pageWidth - margin * 2 - 22
      );

    doc.text(
      filterLines,
      margin + 22,
      y
    );

    y += Math.max(
      7,
      filterLines.length * 3.7 + 3
    );

    // Summary
    doc.setTextColor(...primaryRgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text("SUMMARY", margin, y);

    doc.setTextColor(...bodyTextRgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);

    doc.text(
      `Total Records: ${rows.length}`,
      margin + 22,
      y
    );

    y += 7;

    // Table
    const headers = columns.map(labelize);

    const body = rows.map(
      (row, index) => [
        index + 1,
        ...columns.map(
          (column) =>
            column.includes("date")
              ? formatDate(row?.[column])
              : formatValue(row?.[column])
        ),
      ]
    );

    const tableHead = [
      "#",
      ...headers,
    ];

    const columnStyles =
      isEnquiry
        ? {
            0: {
              cellWidth: 10,
              halign: "center",
            },
            1: { cellWidth: 24 },
            2: { cellWidth: 38 },
            3: { cellWidth: 26 },
            4: { cellWidth: 24 },
            5: { cellWidth: 34 },
            6: { cellWidth: 29 },
            7: { cellWidth: 23 },
            8: { cellWidth: 25 },
          }
        : {
            0: {
              cellWidth: 10,
              halign: "center",
            },
            1: { cellWidth: 48 },
            2: { cellWidth: 42 },
            3: { cellWidth: 31 },
            4: { cellWidth: 70 },
          };

    autoTable(doc, {
      startY: y,
      head: [tableHead],
      body,
      theme: "grid",
      margin: {
        left: margin,
        right: margin,
        top: 40,
        bottom: 14,
      },
      styles: {
        font: "helvetica",
        fontSize: 7.5,
        textColor: bodyTextRgb,
        cellPadding: 2.1,
        valign: "middle",
        overflow: "linebreak",
        lineColor: hexToRgb(
          DEFAULT_PDF_COLORS.border
        ),
        lineWidth: 0.25,
      },
      headStyles: {
        fillColor: primaryRgb,
        textColor: hexToRgb(
          DEFAULT_PDF_COLORS.tableHeaderText
        ),
        font: "helvetica",
        fontStyle: "bold",
        fontSize: 7.5,
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: hexToRgb(
          DEFAULT_PDF_COLORS.alternateRow
        ),
      },
      columnStyles,
      didDrawPage: () => {
        const pageNo =
          doc.internal.getNumberOfPages();

        doc.setFont(
          "helvetica",
          "normal"
        );
        doc.setFontSize(7);
        doc.setTextColor(
          ...hexToRgb(
            DEFAULT_PDF_COLORS.mutedText
          )
        );

        doc.text(
          `${organizationName || "Organization"} | ${branchName || "Branch"} | ${title}`,
          margin,
          pageHeight - 7
        );

        doc.text(
          `Page ${pageNo}`,
          pageWidth - margin,
          pageHeight - 7,
          { align: "right" }
        );
      },
    });

    return doc;
  };

  /* =======================================================
     PDF GENERATION
  ======================================================== */

  const generatePdfForReport =
    async (
      reportType,
      themeColor,
      textColor
    ) => {
      setPdfLoading(true);
      setError("");

      try {
        const rows =
          reportType === "enquiry"
            ? enquiryRows
            : followUpRows;

        if (!rows?.length) {
          notifyError(
            "No records found for the selected filters."
          );
          return;
        }

        const doc =
          createPdf({
            reportType,
            rows,
            themeColor,
            textColor,
          });

        const url =
          window.URL.createObjectURL(
            doc.output("blob")
          );

        const popup =
          window.open(
            url,
            "_blank"
          );

        if (!popup) {
          const link =
            document.createElement(
              "a"
            );

          link.href = url;

          link.download = `${
            reportType ===
            "enquiry"
              ? "Enquiry"
              : "Enquiry_Follow_Up"
          }_Report_${getTodayString()}.pdf`;

          document.body.appendChild(
            link
          );

          link.click();
          link.remove();
        }

        setTimeout(
          () =>
            window.URL.revokeObjectURL(
              url
            ),
          10000
        );

        setColorModalOpen(
          null
        );
      } catch (
        generateError
      ) {
        console.error(
          "PDF generation error:",
          generateError
        );

        notifyError(
          errorMessage(
            generateError
          )
        );
      } finally {
        setPdfLoading(false);
      }
    };

  /* =======================================================
     EXCEL DOWNLOAD
  ======================================================== */

  const downloadExcelFromRows =
    (
      reportType,
      rows
    ) => {
      const isEnquiry =
        reportType ===
        "enquiry";

      const filters =
        isEnquiry
          ? enquiryFilters
          : followUpFilters;

      const excelRows =
        rows.map(
          (row, index) => ({
            "S.No":
              index + 1,

            ...(isEnquiry
              ? {
                  "Branch Name":
                    formatValue(
                      row?.branch_name
                    ),

                  "Enquiry Name":
                    formatValue(
                      row?.enquiry_name
                    ),

                  "Followup Medium":
                    formatValue(
                      row?.followup_medium
                    ),

                  "Source":
                    formatValue(
                      row?.source_name
                    ),

                  "Course":
                    formatValue(
                      row?.course_name
                    ),

                  "Consultant":
                    formatValue(
                      row?.consultant_name
                    ),

                  "Lead Position":
                    formatValue(
                      row?.lead_position
                    ),

                  "Enquiry Date":
                    row?.enquiry_date
                      ? formatDate(
                          row.enquiry_date
                        )
                      : "—",
                }
              : {
                  "Enquiry Name":
                    formatValue(
                      row?.enquiry_name
                    ),

                  "Counsellor Name":
                    formatValue(
                      row?.counsellor_name
                    ),

                  "Follow-Up Date":
                    row?.followup_date
                      ? formatDate(
                          row.followup_date
                        )
                      : "—",

                  Remark:
                    formatValue(
                      row?.remark
                    ),
                }),
          })
        );

      const worksheet =
        XLSX.utils.json_to_sheet(
          excelRows
        );

      worksheet["!cols"] =
        isEnquiry
          ? [
              { wch: 8 },
              { wch: 22 },
              { wch: 28 },
              { wch: 20 },
              { wch: 22 },
              { wch: 24 },
              { wch: 18 },
              { wch: 18 },
              { wch: 18 },
            ]
          : [
              { wch: 8 },
              { wch: 30 },
              { wch: 25 },
              { wch: 18 },
              { wch: 45 },
            ];

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        isEnquiry
          ? "Enquiry Report"
          : "Follow Up Report"
      );

      XLSX.writeFile(
        workbook,
        `${
          isEnquiry
            ? "Enquiry"
            : "Enquiry_Follow_Up"
        }_Report_${filters.dateFrom}_to_${filters.dateTo}.xlsx`
      );
    };

  const downloadBackendFile =
    async (
      reportType,
      fileType
    ) => {
      if (fileType !== "excel") {
        setDownloadOpen(null);
        return;
      }

      setDownloadLoading(true);
      setError("");

      try {
        const isEnquiry =
          reportType === "enquiry";

        const rows =
          isEnquiry
            ? enquiryRows
            : followUpRows;

        if (!rows?.length) {
          notifyError(
            isEnquiry
              ? "No enquiry records found for the selected filters."
              : "No follow-up records found for the selected filters."
          );
          return;
        }

        const payload =
          isEnquiry
            ? enquiryPayload
            : followUpPayload;

        const endpoint =
          isEnquiry
            ? "/api/reports/reports/enquiry-report/export/excel/"
            : "/api/reports/followup-report/export/excel/";

        try {
          const response =
            await API.post(
              endpoint,
              payload,
              {
                responseType: "blob",
              }
            );

          const contentDisposition =
            response.headers?.[
              "content-disposition"
            ] || "";

          const match =
            contentDisposition.match(
              /filename\\*?=(?:UTF-8''|")?([^;"]+)/i
            );

          const defaultName =
            `${
              isEnquiry
                ? "Enquiry"
                : "Enquiry_Follow_Up"
            }_Report_${getTodayString()}.xlsx`;

          const fileName =
            match?.[1]
              ? decodeURIComponent(
                  match[1].replace(
                    /"/g,
                    ""
                  )
                )
              : defaultName;

          const blob =
            new Blob(
              [response.data],
              {
                type:
                  response.headers?.[
                    "content-type"
                  ] ||
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              }
            );

          const url =
            window.URL.createObjectURL(
              blob
            );

          const link =
            document.createElement("a");

          link.href = url;
          link.download = fileName;

          document.body.appendChild(link);
          link.click();
          link.remove();

          window.URL.revokeObjectURL(url);
        } catch (excelApiError) {
          if (
            excelApiError?.response?.status ===
            404
          ) {
            downloadExcelFromRows(
              reportType,
              rows
            );
          } else {
            throw excelApiError;
          }
        }

        setDownloadOpen(null);
      } catch (
        downloadError
      ) {
        console.error(
          `${reportType} excel download error:`,
          downloadError
        );

        notifyError(
          errorMessage(
            downloadError
          )
        );
      } finally {
        setDownloadLoading(false);
      }
    };

  /* =======================================================
     AUTO LOAD REPORT TABLES
     Load both report APIs when the report page is opened
     or the global branch changes.
  ======================================================== */

  useEffect(() => {
    if (!organizationId || !globalBranchId) return;

    fetchEnquiryReport();
    fetchFollowUpReport();
  }, [
    organizationId,
    globalBranchId,
    fetchEnquiryReport,
    fetchFollowUpReport,
  ]);

  /* =======================================================
     OPEN REPORT IN NEW TAB
     Generate Report keeps the current page intact and also
     opens the generated report data in a separate browser tab.
  ======================================================== */

  const escapeHtml = (value) => {
    const stringValue = formatValue(value);
    return String(stringValue)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const openReportInNewTab = useCallback(
    (reportType, rows, popupWindow = null) => {
      if (!rows?.length) {
        notifyError(
          `No ${
            reportType === "enquiry"
              ? "Enquiry"
              : "Enquiry Follow Up"
          } records found for the selected filters.`
        );
        return;
      }

      const isEnquiry = reportType === "enquiry";
      const title = isEnquiry
        ? "Enquiry Report"
        : "Enquiry Follow Up Report";

      const filters = isEnquiry
        ? enquiryFilters
        : followUpFilters;

      const columns = isEnquiry
        ? getEnquiryColumns()
        : getFollowUpColumns();

      const headers = columns.map(labelize);

      const tableHead = headers
        .map(
          (header) =>
            `<th>${escapeHtml(header)}</th>`
        )
        .join("");

      const tableRows = rows
        .map(
          (row, index) => `
            <tr>
              <td>${index + 1}</td>
              ${columns
                .map((column) => {
                  const value = column.includes("date")
                    ? formatDate(row?.[column])
                    : formatValue(row?.[column]);

                  return `<td>${escapeHtml(value)}</td>`;
                })
                .join("")}
            </tr>
          `
        )
        .join("");

      const popup = popupWindow || window.open("", "_blank");

      if (!popup) {
        notifyError(
          "Please allow pop-ups in your browser to open the report in a new tab."
        );
        return;
      }

      popup.document.open();
      popup.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0"
            />
            <title>${escapeHtml(title)}</title>

            <style>
              * {
                box-sizing: border-box;
              }

              body {
                margin: 0;
                padding: 24px;
                font-family:
                  Arial,
                  Helvetica,
                  sans-serif;
                background: #f5f7fb;
                color: #1f2937;
              }

              .report-page {
                max-width: 1600px;
                margin: 0 auto;
              }

              .header {
                background: #fff;
                border: 1px solid #e5e7eb;
                border-radius: 12px;
                padding: 22px 24px;
                margin-bottom: 18px;
              }

              .organization {
                margin: 0;
                color: #16355b;
                font-size: 24px;
                font-weight: 700;
              }

              .branch {
                margin: 6px 0 0;
                font-size: 16px;
                color: #4b5563;
              }

              .title {
                margin: 10px 0 0;
                font-size: 20px;
                font-weight: 700;
                color: #111827;
              }

              .meta {
                margin-top: 12px;
                display: flex;
                flex-wrap: wrap;
                gap: 8px 20px;
                color: #6b7280;
                font-size: 13px;
              }

              .summary {
                background: #fff;
                border: 1px solid #e5e7eb;
                border-radius: 12px;
                padding: 14px 18px;
                margin-bottom: 18px;
                font-size: 14px;
              }

              .table-wrap {
                background: #fff;
                border: 1px solid #e5e7eb;
                border-radius: 12px;
                overflow: auto;
              }

              table {
                width: 100%;
                min-width: 1000px;
                border-collapse: collapse;
              }

              th {
                position: sticky;
                top: 0;
                z-index: 1;
                background: #16355b;
                color: #fff;
                text-align: left;
                padding: 12px 10px;
                font-size: 13px;
                white-space: nowrap;
              }

              td {
                padding: 10px;
                border-bottom: 1px solid #e5e7eb;
                font-size: 13px;
                vertical-align: top;
              }

              tbody tr:hover {
                background: #f8fafc;
              }

              .footer {
                margin-top: 14px;
                color: #6b7280;
                font-size: 12px;
              }

              @media print {
                body {
                  background: #fff;
                  padding: 0;
                }

                .header,
                .summary,
                .table-wrap {
                  border: 0;
                }

                .table-wrap {
                  overflow: visible;
                }

                th {
                  position: static;
                }
              }
            </style>
          </head>

          <body>
            <div class="report-page">
              <div class="header">
                <h1 class="organization">
                  ${escapeHtml(organizationName || "Organization")}
                </h1>

                <div class="branch">
                  ${escapeHtml(branchName || "Branch")}
                </div>

                <div class="title">
                  ${escapeHtml(title)}
                </div>

                <div class="meta">
                  <span>
                    From:
                    ${escapeHtml(filters.dateFrom || "All")}
                  </span>

                  <span>
                    To:
                    ${escapeHtml(filters.dateTo || "All")}
                  </span>

                  <span>
                    Generated:
                    ${escapeHtml(
                      new Date().toLocaleString("en-IN")
                    )}
                  </span>
                </div>
              </div>

              <div class="summary">
                <strong>Total Records:</strong>
                ${rows.length}
              </div>

              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      ${tableHead}
                    </tr>
                  </thead>

                  <tbody>
                    ${tableRows}
                  </tbody>
                </table>
              </div>

              <div class="footer">
                ${escapeHtml(
                  organizationName || "Organization"
                )}
                &nbsp;|&nbsp;
                ${escapeHtml(branchName || "Branch")}
                &nbsp;|&nbsp;
                ${escapeHtml(title)}
              </div>
            </div>
          </body>
        </html>
      `);
      popup.document.close();
      popup.focus();
    },
    [
      enquiryFilters,
      followUpFilters,
      getEnquiryColumns,
      getFollowUpColumns,
      branchName,
      organizationName,
      notifyError,
    ]
  );

  const handleGenerateEnquiryReport = useCallback(
    async () => {
      const rows = await fetchEnquiryReport();

      if (!rows?.length) {
        notifyError(
          "No enquiry records found for the selected filters."
        );
        return;
      }

      setTempPdfThemeColor(
        normalizeHex(
          pdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        )
      );

      setTempPdfTextColor(
        normalizeHex(
          pdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        )
      );

      setColorModalOpen("enquiry");
    },
    [fetchEnquiryReport, notifyError, pdfThemeColor, pdfTextColor]
  );

  const handleGenerateFollowUpReport = useCallback(
    async () => {
      const rows = await fetchFollowUpReport();

      if (!rows?.length) {
        notifyError(
          "No follow-up records found for the selected filters."
        );
        return;
      }

      setTempPdfThemeColor(
        normalizeHex(
          pdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        )
      );

      setTempPdfTextColor(
        normalizeHex(
          pdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        )
      );

      setColorModalOpen("followup");
    },
    [fetchFollowUpReport, notifyError, pdfThemeColor, pdfTextColor]
  );

  /* =======================================================
     ACTIVE REPORT DETAILS
  ======================================================== */

  const enquiryBranchLabel =
    getSelectedBranchLabel(
      globalBranchId,
      enquiryRows
    );

  const followUpBranchLabel =
    getSelectedBranchLabel(
      globalBranchId,
      followUpRows
    );

  /* =======================================================
     RENDER
  ======================================================== */

  return (
    <Box
      sx={{
        p: {
          xs: 1.5,
          md: 3,
        },
      }}
    >
  {/* =================================================
    REPORT TABS
================================================= */}

<Box
  sx={{
    mb: 2,
    p: 0.5,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflowX: "auto",
  }}
>
  <Tabs
    value={reportTab}
    onChange={(_, value) =>
      setReportTab(value)
    }
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 42,

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTabs-flexContainer": {
        gap: 0.5,
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: "auto",
        px: {
          xs: 1.5,
          sm: 2,
        },
        borderRadius: 1.5,
        color: "text.primary",
        fontWeight: 600,
        textTransform: "none",
        fontSize: {
          xs: 13,
          sm: 14,
        },
        opacity: 1,
        transition: "all 0.2s ease",

        "&:hover": {
          color: "text.primary",
          backgroundColor: "action.hover",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },

      "& .MuiTabs-scrollButtons": {
        color: "text.primary",

        "&.Mui-disabled": {
          opacity: 0.35,
        },
      },
    }}
  >
    <Tab
      value="enquiry"
      label="Enquiry Report"
    />

    <Tab
      value="followup"
      label="Enquiry Follow Up Report"
    />
  </Tabs>
</Box>
      {/* =================================================
          ERROR
      ================================================= */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
          }}
          onClose={() =>
            setError("")
          }
        >
          {error}
        </Alert>
      )}

      {/* =================================================
          ENQUIRY
      ================================================= */}

      {reportTab ===
        "enquiry" && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 0,
            backgroundColor: "unset",
            boxShadow: "none",
          }}
        >
          <ReportHeader
            title="Enquiry Report"
            subtitle={`${organizationName} • ${enquiryBranchLabel} • ${enquiryFilters.dateFrom} to ${enquiryFilters.dateTo}`}
            onFilter={() =>
              setEnquiryFilterOpen(
                true
              )
            }
            onGenerate={
              handleGenerateEnquiryReport
            }
            onExcel={() =>
              setDownloadOpen(
                "enquiry"
              )
            }
            onReset={
              resetEnquiryFilters
            }
            loading={
              enquiryLoading
            }
            disableExport={
              !enquiryRows.length
            }
          />

   

          <CardContent
            sx={{
              p: {
                xs: 1.5,
                md: 2,
              },
              "&:last-child": {
                pb: {
                  xs: 1.5,
                  md: 2,
                },
              },
            }}
          >
            <ReportTable
              title="Enquiry records"
              rows={enquiryRows}
              columns={
                getEnquiryColumns()
              }
              loading={
                enquiryLoading
              }
              search={
                enquirySearch
              }
              onSearch={
                setEnquirySearch
              }
            />
          </CardContent>
        </Card>
      )}

      {/* =================================================
          FOLLOW UP
      ================================================= */}

      {reportTab ===
        "followup" && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
          }}
        >
          <ReportHeader
            title="Enquiry Follow Up Report"
            subtitle={`${organizationName} • ${followUpBranchLabel} • ${followUpFilters.dateFrom} to ${followUpFilters.dateTo}`}
            onFilter={() =>
              setFollowUpFilterOpen(
                true
              )
            }
            onGenerate={
              handleGenerateFollowUpReport
            }
            onExcel={() =>
              setDownloadOpen(
                "followup"
              )
            }
            onReset={
              resetFollowUpFilters
            }
            loading={
              followUpLoading
            }
            disableExport={
              !followUpRows.length
            }
          />


          <CardContent
            sx={{
              p: {
                xs: 1.5,
                md: 2,
              },
              "&:last-child": {
                pb: {
                  xs: 1.5,
                  md: 2,
                },
              },
            }}
          >
            <ReportTable
              title="Follow-up records"
              rows={
                followUpRows
              }
              columns={
                getFollowUpColumns()
              }
              loading={
                followUpLoading
              }
              search={
                followUpSearch
              }
              onSearch={
                setFollowUpSearch
              }
            />
          </CardContent>
        </Card>
      )}

      {/* =================================================
          ENQUIRY FILTER
      ================================================= */}

      <FilterModal
        open={
          enquiryFilterOpen
        }
        onClose={() =>
          setEnquiryFilterOpen(
            false
          )
        }
        title="Enquiry Report"
         dateFrom={
          enquiryFilters.dateFrom
        }
        dateTo={
          enquiryFilters.dateTo
        }
        onDateFromChange={(
          value
        ) =>
          setEnquiryFilters(
            (prev) => ({
              ...prev,
              dateFrom:
                value,
            })
          )
        }
        onDateToChange={(
          value
        ) =>
          setEnquiryFilters(
            (prev) => ({
              ...prev,
              dateTo:
                value,
            })
          )
        }
        selectedId={
          enquiryFilters.selectedId
        }
        onSelectedIdChange={(
          value
        ) =>
          setEnquiryFilters(
            (prev) => ({
              ...prev,
              selectedId:
                value,
            })
          )
        }
        options={
          enquiryOptions
        }
        loadingOptions={
          enquiryOptionsLoading
        }
        selectionLabel="Enquiry"
        onReset={
          resetEnquiryFilters
        }
        onApply={() => {
          setEnquiryFilterOpen(
            false
          );
          fetchEnquiryReport();
        }}
      />

      {/* =================================================
          FOLLOW-UP FILTER
      ================================================= */}

      <FilterModal
        open={
          followUpFilterOpen
        }
        onClose={() =>
          setFollowUpFilterOpen(
            false
          )
        }
        title="Enquiry Follow Up Report"
         dateFrom={
          followUpFilters.dateFrom
        }
        dateTo={
          followUpFilters.dateTo
        }
        onDateFromChange={(
          value
        ) =>
          setFollowUpFilters(
            (prev) => ({
              ...prev,
              dateFrom:
                value,
            })
          )
        }
        onDateToChange={(
          value
        ) =>
          setFollowUpFilters(
            (prev) => ({
              ...prev,
              dateTo:
                value,
            })
          )
        }
        selectedId={
          followUpFilters.selectedId
        }
        onSelectedIdChange={(
          value
        ) =>
          setFollowUpFilters(
            (prev) => ({
              ...prev,
              selectedId:
                value,
            })
          )
        }
        options={
          followUpOptions
        }
        loadingOptions={
          followUpOptionsLoading
        }
        selectionLabel="Follow Up"
        onReset={
          resetFollowUpFilters
        }
        onApply={() => {
          setFollowUpFilterOpen(
            false
          );
          fetchFollowUpReport();
        }}
      />

      {/* =================================================
          ENQUIRY PDF COLOR
      ================================================= */}

      <ColorPickerModal reportTitle={"Enquiry Report"}
        open={
          colorModalOpen ===
          "enquiry"
        }
        loading={pdfLoading}
        tempThemeColor={
          tempPdfThemeColor
        }
        tempTextColor={
          tempPdfTextColor
        }
        onClose={() =>
          setColorModalOpen(
            null
          )
        }
        onThemeChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              DEFAULT_PDF_COLORS.primary
            );

          setTempPdfThemeColor(
            next
          );
        }}
        onTextChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              DEFAULT_PDF_COLORS.bodyText
            );

          setTempPdfTextColor(
            next
          );
        }}
        onReset={() => {
          setTempPdfThemeColor(
            normalizeHex(
              pdfThemeColor,
              DEFAULT_PDF_COLORS.primary
            )
          );

          setTempPdfTextColor(
            normalizeHex(
              pdfTextColor,
              DEFAULT_PDF_COLORS.bodyText
            )
          );
        }}
        onGenerate={() => {
          updatePdfThemeColor?.(
            tempPdfThemeColor
          );

          updatePdfTextColor?.(
            tempPdfTextColor
          );

          generatePdfForReport(
            "enquiry",
            tempPdfThemeColor,
            tempPdfTextColor
          );
        }}
      />

      {/* =================================================
          FOLLOW-UP PDF COLOR
      ================================================= */}

      <ColorPickerModal reportTitle={"Enquiry Follow-up Report"}
        open={
          colorModalOpen ===
          "followup"
        }
        loading={pdfLoading}
        tempThemeColor={
          tempPdfThemeColor
        }
        tempTextColor={
          tempPdfTextColor
        }
        onClose={() =>
          setColorModalOpen(
            null
          )
        }
        onThemeChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              DEFAULT_PDF_COLORS.primary
            );

          setTempPdfThemeColor(
            next
          );
        }}
        onTextChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              DEFAULT_PDF_COLORS.bodyText
            );

          setTempPdfTextColor(
            next
          );
        }}
        onReset={() => {
          setTempPdfThemeColor(
            normalizeHex(
              pdfThemeColor,
              DEFAULT_PDF_COLORS.primary
            )
          );

          setTempPdfTextColor(
            normalizeHex(
              pdfTextColor,
              DEFAULT_PDF_COLORS.bodyText
            )
          );
        }}
        onGenerate={() => {
          updatePdfThemeColor?.(
            tempPdfThemeColor
          );

          updatePdfTextColor?.(
            tempPdfTextColor
          );

          generatePdfForReport(
            "followup",
            tempPdfThemeColor,
            tempPdfTextColor
          );
        }}
      />

      {/* =================================================
          ENQUIRY DOWNLOAD
      ================================================= */}

      <DownloadModal
        open={
          downloadOpen ===
          "enquiry"
        }
        onClose={() =>
          setDownloadOpen(
            null
          )
        }
        title="Download Enquiry Report"
        loading={
          downloadLoading
        }
        onExcel={() =>
          downloadBackendFile(
            "enquiry",
            "excel"
          )
        }
      />

      {/* =================================================
          FOLLOW-UP DOWNLOAD
      ================================================= */}

      <DownloadModal
        open={
          downloadOpen ===
          "followup"
        }
        onClose={() =>
          setDownloadOpen(
            null
          )
        }
        title="Download Enquiry Follow Up Report"
        loading={
          downloadLoading
        }
        onExcel={() =>
          downloadBackendFile(
            "followup",
            "excel"
          )
        }
      />
    </Box>
  );
}
